import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, 
  X, 
  Rocket, 
  Sparkles, 
  Users, 
  Target, 
  Gamepad2, 
  Mail, 
  Send,
  Lightbulb,
  Heart,
  Zap,
  CheckCircle
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dcfab846-7111-41ba-aa17-e89feee994c2/artifacts/0k8yc3qi_piximyth.png";

// Animated Space Background Component with Custom Image
const SpaceBackground = () => {
  return (
    <div className="space-background" data-testid="space-background">
      <div className="space-bg-image" />
      <div className="space-bg-overlay" />
      
      <div className="stars-animated">
        <div className="twinkle-star large" style={{ top: '8%', left: '12%', animationDelay: '0s' }} />
        <div className="twinkle-star" style={{ top: '15%', left: '35%', animationDelay: '0.8s' }} />
        <div className="twinkle-star small" style={{ top: '22%', left: '65%', animationDelay: '1.5s' }} />
        <div className="twinkle-star" style={{ top: '30%', left: '18%', animationDelay: '2.2s' }} />
        <div className="twinkle-star large" style={{ top: '45%', left: '78%', animationDelay: '0.5s' }} />
        <div className="twinkle-star small" style={{ top: '55%', left: '8%', animationDelay: '1.8s' }} />
        <div className="twinkle-star" style={{ top: '65%', left: '42%', animationDelay: '2.8s' }} />
        <div className="twinkle-star small" style={{ top: '75%', left: '88%', animationDelay: '1.2s' }} />
        <div className="twinkle-star" style={{ top: '85%', left: '25%', animationDelay: '3.5s' }} />
        <div className="twinkle-star large" style={{ top: '92%', left: '55%', animationDelay: '0.3s' }} />
      </div>
      
      <div className="dust-particle" style={{ left: '10%', animationDelay: '0s', animationDuration: '25s' }} />
      <div className="dust-particle" style={{ left: '25%', animationDelay: '5s', animationDuration: '22s' }} />
      <div className="dust-particle" style={{ left: '45%', animationDelay: '8s', animationDuration: '28s' }} />
      <div className="dust-particle" style={{ left: '65%', animationDelay: '3s', animationDuration: '20s' }} />
      <div className="dust-particle" style={{ left: '80%', animationDelay: '12s', animationDuration: '24s' }} />
      <div className="dust-particle" style={{ left: '92%', animationDelay: '7s', animationDuration: '26s' }} />
      
      <div className="space-particle" style={{ top: '20%', left: '15%', animationDelay: '0s' }} />
      <div className="space-particle" style={{ top: '40%', left: '70%', animationDelay: '10s' }} />
      <div className="space-particle" style={{ top: '60%', left: '30%', animationDelay: '5s' }} />
      <div className="space-particle" style={{ top: '80%', left: '85%', animationDelay: '15s' }} />
      
      <div className="shooting-star" style={{ top: '18%', left: '5%', animationDelay: '0s' }} />
      <div className="shooting-star" style={{ top: '50%', left: '20%', animationDelay: '8s' }} />
    </div>
  );
};

// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", href: "#home" },
    { name: "Games", href: "#games" },
    { name: "About", href: "#about" },
    { name: "Vision", href: "#vision" },
    { name: "Contact", href: "#contact" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? "navbar-glass shadow-pixel" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <a 
            href="#home" 
            onClick={(e) => { e.preventDefault(); scrollToSection("#home"); }}
            className="logo-hover"
            data-testid="logo-link"
          >
            <img 
              src={LOGO_URL} 
              alt="PixiMyth" 
              className="h-10 sm:h-12"
            />
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className="nav-link whitespace-nowrap"
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </button>
            ))}
          </div>

          <button
            className="md:hidden p-2 text-pixi-brown"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden navbar-glass border-t-2 border-pixi-brown"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className="block w-full text-left nav-link py-2"
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToGames = () => {
    const element = document.querySelector("#games");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      id="home" 
      className="min-h-screen flex items-center relative overflow-hidden pt-20"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="z-10 max-w-2xl"
        >
          <h1 className="font-pixel text-pixi-brown text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-relaxed mb-6 text-shadow-pixel">
            Forging Legends<br />in Pixels
          </h1>
          <p className="body-text text-pixi-text-muted mb-8 max-w-lg text-lg sm:text-xl">
            The next generation of mobile gaming is here. We craft retro-inspired experiences 
            that blend nostalgia with modern gameplay.
          </p>
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={scrollToGames}
              className="pixel-button flex items-center gap-2"
              data-testid="cta-view-games"
            >
              <Gamepad2 size={18} />
              View Games
            </button>
            <button 
              onClick={scrollToContact}
              className="pixel-button-secondary flex items-center gap-2"
              data-testid="cta-contact-us"
            >
              <Mail size={18} />
              Contact Us
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Featured Game Section
const GamesSection = () => {
  return (
    <section 
      id="games" 
      className="py-24 relative"
      data-testid="games-section"
    >
      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="section-title mb-4">Featured Game</h2>
          <div className="w-24 h-1 bg-pixi-orange mx-auto" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="pixel-card game-card max-w-4xl mx-auto"
          data-testid="game-card-space-rumble"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="relative">
              <div className="aspect-square border-2 border-pixi-brown overflow-hidden relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pixi-arcade/artifacts/4ysfmz7j_space%20rumble%20kapak.png"
                  alt="Space Rumble"
                  className="w-full h-full object-cover"
                  data-testid="space-rumble-image"
                />
              </div>
              <div 
                className="absolute -top-3 -right-3 bg-pixi-orange border-2 border-pixi-brown px-4 py-2 shadow-pixel coming-soon-badge"
                data-testid="coming-soon-badge"
              >
                <span className="font-pixel text-[10px] text-pixi-brown">COMING SOON</span>
              </div>
            </div>

            <div>
              <h3 className="font-pixel text-pixi-brown text-lg sm:text-xl mb-4">
                Space Rumble
              </h3>
              <p className="body-text text-pixi-text-muted mb-6">
                A retro-futuristic arcade shooter reimagined for modern touchscreens. 
                Experience fast-paced space combat with pixel-perfect controls and 
                challenging gameplay that rewards skill.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="font-mono text-sm bg-pixi-cream border border-pixi-brown px-3 py-1">
                  Arcade
                </span>
                <span className="font-mono text-sm bg-pixi-cream border border-pixi-brown px-3 py-1">
                  Space
                </span>
                <span className="font-mono text-sm bg-pixi-cream border border-pixi-brown px-3 py-1">
                  Mobile
                </span>
              </div>
              <button 
                className="pixel-button flex items-center gap-2"
                data-testid="notify-me-btn"
                onClick={() => toast.success("You'll be notified when Space Rumble launches!")}
              >
                <Sparkles size={18} />
                Notify Me
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Who We Are Section
const AboutSection = () => {
  return (
    <section 
      id="about" 
      className="py-24 relative"
      data-testid="about-section"
    >
      <div className="absolute inset-0 bg-pixi-cream/90 backdrop-blur-sm" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="section-title mb-4">Who We Are</h2>
          <div className="w-24 h-1 bg-pixi-orange mx-auto" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="body-text text-pixi-text mb-6 text-lg">
              <span className="font-pixel text-pixi-orange">PixiMyth</span> is an indie game 
              studio passionate about creating memorable mobile gaming experiences. We believe 
              in the magic of retro aesthetics combined with modern gameplay mechanics.
            </p>
            <p className="body-text text-pixi-text-muted mb-6">
              Our team of dedicated developers and artists work tirelessly to craft games 
              that are not just fun to play, but also beautiful to experience. Every pixel 
              is placed with purpose, every mechanic refined for satisfaction.
            </p>
            <p className="body-text text-pixi-text-muted">
              We focus on quality over quantity, ensuring each title we release meets our 
              high standards for gameplay, performance, and player enjoyment.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="pixel-card text-center p-6" data-testid="about-card-passion">
              <Gamepad2 size={40} className="text-pixi-orange mx-auto mb-4" />
              <h4 className="font-pixel text-xs text-pixi-brown mb-2">Passion</h4>
              <p className="text-sm text-pixi-text-muted font-body">
                We love what we do
              </p>
            </div>
            <div className="pixel-card text-center p-6" data-testid="about-card-quality">
              <Target size={40} className="text-pixi-orange mx-auto mb-4" />
              <h4 className="font-pixel text-xs text-pixi-brown mb-2">Quality</h4>
              <p className="text-sm text-pixi-text-muted font-body">
                Every detail matters
              </p>
            </div>
            <div className="pixel-card text-center p-6" data-testid="about-card-competition">
              <Rocket size={40} className="text-pixi-orange mx-auto mb-4" />
              <h4 className="font-pixel text-xs text-pixi-brown mb-2">Competition</h4>
              <p className="text-sm text-pixi-text-muted font-body">
                Skill-based challenges
              </p>
            </div>
            <div className="pixel-card text-center p-6" data-testid="about-card-fun">
              <Sparkles size={40} className="text-pixi-orange mx-auto mb-4" />
              <h4 className="font-pixel text-xs text-pixi-brown mb-2">Fun</h4>
              <p className="text-sm text-pixi-text-muted font-body">
                Entertainment first
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Vision Section
const VisionSection = () => {
  return (
    <section 
      id="vision" 
      className="py-24 relative overflow-hidden"
      data-testid="vision-section"
    >
      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="section-title mb-4">Our Vision</h2>
          <div className="w-24 h-1 bg-pixi-orange mx-auto" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-4xl mx-auto text-center"
        >
          <p className="font-pixel text-pixi-brown text-base sm:text-lg md:text-xl leading-loose mb-8">
            "Reviving the golden age of gaming, one pixel at a time."
          </p>
          <p className="body-text text-pixi-text-muted text-lg max-w-2xl mx-auto mb-12">
            We envision a world where mobile games deliver the same depth, challenge, 
            and satisfaction as the classics we grew up with. Our mission is to create 
            pixel-perfect gameplay experiences that reward skill and dedication.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16"
        >
          <div className="pixel-card value-card text-center" data-testid="value-innovation">
            <Lightbulb size={48} className="text-pixi-orange mx-auto mb-4" />
            <h4 className="font-pixel text-xs text-pixi-brown mb-3">Innovation</h4>
            <p className="text-sm text-pixi-text-muted font-body">
              Pushing boundaries while honoring traditions
            </p>
          </div>
          
          <div className="pixel-card value-card text-center" data-testid="value-player-first">
            <Heart size={48} className="text-pixi-orange mx-auto mb-4" />
            <h4 className="font-pixel text-xs text-pixi-brown mb-3">Player-First</h4>
            <p className="text-sm text-pixi-text-muted font-body">
              Every decision centered on player experience
            </p>
          </div>
          
          <div className="pixel-card value-card text-center" data-testid="value-performance">
            <Zap size={48} className="text-pixi-orange mx-auto mb-4" />
            <h4 className="font-pixel text-xs text-pixi-brown mb-3">Performance</h4>
            <p className="text-sm text-pixi-text-muted font-body">
              Buttery smooth 60fps on every device
            </p>
          </div>
          
          <div className="pixel-card value-card text-center" data-testid="value-community">
            <Users size={48} className="text-pixi-orange mx-auto mb-4" />
            <h4 className="font-pixel text-xs text-pixi-brown mb-3">Community</h4>
            <p className="text-sm text-pixi-text-muted font-body">
              Building connections through shared experiences
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section with Email Sending
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.message.trim()) {
      toast.error("Please enter your message");
      return;
    }

    if (formData.message.length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitSuccess(true);
        if (response.data.email_sent) {
          toast.success("Message sent successfully! We'll get back to you soon.", {
            icon: <Rocket className="text-pixi-orange" size={20} />
          });
        } else {
          toast.success("Message received! We'll get back to you soon.");
        }
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setSubmitSuccess(false), 2000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to send message. Please try again.";
      toast.error(errorMessage);
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="py-24 relative"
      data-testid="contact-section"
    >
      <div className="absolute inset-0 bg-pixi-cream/90 backdrop-blur-sm" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="section-title mb-4">Get In Touch</h2>
          <div className="w-24 h-1 bg-pixi-orange mx-auto" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="font-pixel text-xs text-pixi-brown mb-2 block">
                  Your Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your name"
                  className="pixel-input"
                  data-testid="input-name"
                  maxLength={100}
                />
              </div>
              
              <div>
                <label className="font-pixel text-xs text-pixi-brown mb-2 block">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="pixel-input"
                  data-testid="input-email"
                />
              </div>
              
              <div>
                <label className="font-pixel text-xs text-pixi-brown mb-2 block">
                  Message
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us about your project or inquiry..."
                  className="pixel-input pixel-textarea"
                  rows={5}
                  data-testid="input-message"
                  maxLength={2000}
                />
                <p className="text-xs text-pixi-text-muted mt-1 font-body">
                  {formData.message.length}/2000 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`pixel-button w-full flex items-center justify-center gap-2 disabled:opacity-50 ${
                  submitSuccess ? 'send-success' : ''
                }`}
                data-testid="submit-contact-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-pixi-brown border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : submitSuccess ? (
                  <>
                    <CheckCircle size={18} />
                    Sent!
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-8"
          >
            <div className="pixel-card" data-testid="contact-info-card">
              <h4 className="font-pixel text-xs text-pixi-brown mb-4">
                Direct Contact
              </h4>
              <a 
                href="mailto:hello@piximyth.com" 
                className="flex items-center gap-3 text-pixi-text hover:text-pixi-orange transition-colors"
                data-testid="email-link"
              >
                <Mail size={24} className="text-pixi-orange" />
                <span className="font-body text-lg">hello@piximyth.com</span>
              </a>
            </div>

            <div className="pixel-card" data-testid="social-links-card">
              <h4 className="font-pixel text-xs text-pixi-brown mb-4">
                Follow Us
              </h4>
              <div className="flex gap-4">
                <a 
                  href="https://www.linkedin.com/company/piximyth" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="pixel-button-secondary p-3 social-link"
                  data-testid="social-linkedin"
                  aria-label="LinkedIn"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/piximyth/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="pixel-button-secondary p-3 social-link"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
                <a 
                  href="https://www.tiktok.com/@piximyth" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="pixel-button-secondary p-3 social-link"
                  data-testid="social-tiktok"
                  aria-label="TikTok"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>

            <div className="pixel-card bg-pixi-orange/10">
              <h4 className="font-pixel text-xs text-pixi-brown mb-3">
                Partnership Inquiries
              </h4>
              <p className="text-sm text-pixi-text-muted font-body">
                Interested in collaborating or publishing? We'd love to hear from you. 
                Drop us a message and let's create something amazing together.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-pixi-brown text-pixi-cream py-8 relative z-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PixiMyth" className="h-8" />
          </div>
          <p className="font-body text-sm text-pixi-cream/70">
            © {currentYear} PixiMyth. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#home" className="text-sm text-pixi-cream/70 hover:text-pixi-orange transition-colors">
              Home
            </a>
            <a href="#games" className="text-sm text-pixi-cream/70 hover:text-pixi-orange transition-colors">
              Games
            </a>
            <a href="#contact" className="text-sm text-pixi-cream/70 hover:text-pixi-orange transition-colors">
              Contact
            </a>
            <a href="/privacy-policy_SpaceRumble/" className="text-sm text-pixi-cream/70 hover:text-pixi-orange transition-colors">
              Space Rumble - Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <SpaceBackground />
        
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#fff8e7',
              border: '2px solid #5f2e00',
              color: '#5f2e00',
              fontFamily: '"Press Start 2P", cursive',
              fontSize: '10px',
            },
          }}
        />
        <Navbar />
        <main className="content-wrapper">
          <HeroSection />
          <GamesSection />
          <AboutSection />
          <VisionSection />
          <ContactSection />
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
